import React, { useState, useEffect } from 'react';
import {
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Box,
    Modal,
    FormHelperText
} from '@mui/material';

import config from '../../config';
import { notifyError, notifySuccess } from '../../util';
import { translate } from '../../i18n/customI18nProvider';
import { useNotify } from 'react-admin';
import { addBarPossibleDepositReturns, removeBarPossibleDepositReturns, swapBarPossibleDepositReturns } from '../../services/bar.service';
import { Close, Delete, Save } from '@mui/icons-material';

const DepositReturnForm = ({ currencyCode, show, depositReturn, depositReturns, selectedReturns, onClose, barId }) => {
    const notify = useNotify();
    const [depositReturnId, setDepositReturnId] = useState(depositReturn ? depositReturn.id : '');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (depositReturn) setDepositReturnId(depositReturn.id);
        else setDepositReturnId('');
    }, [show]);

    const handleChange = (value) => setDepositReturnId(value);

    const confirmForm = () => {
        let hasError = false;
        let newErrors = {};
        if (!depositReturnId || depositReturnId.trim() === '') {
            newErrors.depositReturn = translate('custom.errors.required_field');
            hasError = true;
        }
        setErrors(newErrors);
        return !hasError;
    };

    const handleSave = async () => {
        if (!confirmForm()) return;
        let response;
        if (depositReturn) {
            response = await swapBarPossibleDepositReturns(barId, { old_deposit_id: depositReturn.id, new_deposit_id: depositReturnId });
        } else {
            response = await addBarPossibleDepositReturns(barId, { deposit_id: depositReturnId });
        }

        if (response.error) notifyError(notify, response.error.message || response.error);
        else notifySuccess('custom.saved_successfully');

        onClose();
    };

    const handleDelete = async () => {
        const response = await removeBarPossibleDepositReturns(barId, { deposit_id: depositReturn.id });
        if (response.error) {
            notifyError(notify, response.error);
        } else {
            notifySuccess('custom.deleted_successfully');
        }
        onClose();
    };

    const availableReturns = () => {
        const available = [];
        for(const dep of depositReturns) {
            let alreadySelected = false;
            for(const id of selectedReturns) {
                if(dep.id == id) {
                    alreadySelected = true;
                    break;
                }
            }
            if(!alreadySelected || (depositReturn && dep.id == depositReturn.id)) {
                available.push(dep);
            }
        }
        return available;
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: 800,
        maxHeight: '80vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2
    };

    return (
        <Modal open={show} onClose={onClose}>
            <Box sx={modalStyle}>
                <h3 style={{ marginTop: 0, marginBottom: 30, borderBottom: '2px solid ' + config.baseColor, color: config.baseColor }}>
                    {depositReturn ? translate('custom.edit_deposit_return') : translate('custom.add_deposit_return')}
                </h3>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth variant="standard" error={!!errors.depositReturn}>
                            <InputLabel>{translate('custom.deposit_return')}</InputLabel>
                            <Select value={depositReturnId} onChange={(e) => handleChange(e.target.value)}>
                                {availableReturns().map(dep => (
                                    <MenuItem key={dep.id} value={dep.id}>
                                        <p style={{ margin: 0, fontSize: 13 }}>{dep.name} {translate('custom.of')} {dep.price} {currencyCode}</p>
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.depositReturn}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    {depositReturn && (
                        <Button onClick={handleDelete} color="error" variant="outlined" startIcon={<Delete />}>
                            {translate('custom.delete')}
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button onClick={onClose} variant="outlined" startIcon={<Close />}>
                            {translate('custom.cancel')}
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSave} startIcon={<Save />}>
                            {translate('custom.save')}
                        </Button>
                    </div>
                </Box>
            </Box>
        </Modal>
    );
};

export default DepositReturnForm;
